import {
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import { ReportWidgetChartTooltip } from './ReportWidgetChartTooltip';

import type {
    ReportWidgetItem,
    ReportWidgetTooltipRowsBuilder,
} from '../../model/types';
import type { LegendProps } from 'recharts';
import type { CurveType } from 'recharts/types/shape/Curve';

export interface ReportWidgetLineChartDataPoint {
    name: string;
    id: string;
    value1: number;
    value2: number;
}

interface ReportWidgetLineChartProps {
    data: ReportWidgetLineChartDataPoint[];
    showLegend: boolean;
    lineNames: [string, string];
    tooltipFormatter: (value: number) => string;
    tooltipRowsBuilder?: ReportWidgetTooltipRowsBuilder;
    width?: number;
    height?: number;
    lineType?: CurveType;
    disableAnimation?: boolean;
    xAxisTickFormatter?: (
        label: string | number,
        index: number,
        data: ReportWidgetLineChartDataPoint[],
    ) => string;
    yAxisTickFormatter?: (value: number) => string;
}

const DEFAULT_LINE_COLORS: [string, string] = ['#4BA054', '#B2B2B2'];

const ReportWidgetLineChartLegend = ({ payload }: LegendProps) => {
    if (!payload) {
        return null;
    }

    return (
        <ul
            style={{
                listStyle: 'none',
                margin: 0,
                padding: 0,
                display: 'flex',
                gap: '20px',
                paddingBottom: '24px',
                fontSize: '13px',
                fontWeight: 400,
                lineHeight: '20px',
                color: '#000000',
            }}
        >
            {payload.map((entry) => (
                <li
                    key={entry.value as string}
                    style={{ display: 'flex', alignItems: 'center' }}
                >
                    <span
                        style={{
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            backgroundColor: entry.color || '#000000',
                            marginRight: '8px',
                        }}
                    />
                    <span>{entry.value}</span>
                </li>
            ))}
        </ul>
    );
};

export const ReportWidgetLineChart = ({
    data,
    showLegend,
    lineNames,
    tooltipFormatter,
    tooltipRowsBuilder,
    width,
    height,
    lineType = 'linear',
    disableAnimation = false,
    xAxisTickFormatter,
    yAxisTickFormatter,
}: ReportWidgetLineChartProps) => (
    <LineChart data={data} width={width} height={height}>
        <CartesianGrid stroke="#E0E0E0" strokeWidth={1} />
        <XAxis
            dataKey="name"
            tick={{ fontSize: 12, fill: '#000000' }}
            tickLine={{ stroke: '#E0E0E0', strokeWidth: 1 }}
            tickSize={10}
            tickMargin={6}
            tickFormatter={(value: string | number, index: number) =>
                xAxisTickFormatter
                    ? xAxisTickFormatter(value, index, data)
                    : String(value)
            }
            axisLine={{ stroke: '#E0E0E0', strokeWidth: 1 }}
            padding={{ left: 30, right: 30 }}
        />
        <YAxis
            tick={{ fontSize: 12, fill: '#000000' }}
            tickLine={{ stroke: '#E0E0E0', strokeWidth: 1 }}
            tickSize={10}
            tickMargin={6}
            tickFormatter={(v: number) => {
                if (yAxisTickFormatter) {
                    return yAxisTickFormatter(v);
                }

                if (typeof v !== 'number') {
                    return String(v);
                }
                if (v === 0) {
                    return '0';
                }

                return tooltipFormatter(v);
            }}
            domain={[
                'auto',
                (dataMax: number) =>
                    Number.isFinite(dataMax) ? dataMax * 1.2 : dataMax,
            ]}
            axisLine={{ stroke: '#E0E0E0', strokeWidth: 1 }}
        />
        <Tooltip
            content={
                <ReportWidgetChartTooltip
                    tooltipFormatter={tooltipFormatter}
                    total={0}
                    // Line charts currently do not use ReportWidgetItem context for tooltips.
                    items={[] as ReportWidgetItem[]}
                    tooltipRowsBuilder={tooltipRowsBuilder}
                />
            }
        />
        {showLegend && (
            <Legend
                verticalAlign="top"
                align="left"
                iconType="circle"
                content={<ReportWidgetLineChartLegend />}
            />
        )}
        <Line
            type={lineType}
            dataKey="value1"
            name={lineNames[0]}
            stroke={DEFAULT_LINE_COLORS[0]}
            strokeWidth={2}
            dot={{ r: 2.5, strokeWidth: 2 }}
            activeDot={{ r: 5, strokeWidth: 2 }}
            isAnimationActive={!disableAnimation}
            legendType="circle"
        />
        <Line
            type={lineType}
            dataKey="value2"
            name={lineNames[1]}
            stroke={DEFAULT_LINE_COLORS[1]}
            strokeWidth={2}
            dot={{ r: 2.5, strokeWidth: 2 }}
            activeDot={{ r: 5, strokeWidth: 2 }}
            isAnimationActive={!disableAnimation}
            legendType="circle"
        />
    </LineChart>
);
